<template>
  <div>
    <v-card>
      <v-card-title>{{ $t('Work Process Record') }}</v-card-title>
      <v-card-text>
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">{{ $t('Date') }}</th>
                <th class="text-left">{{ $t('Result') }}</th>
                <th class="text-left">{{ $t('Detail') }}</th>
                <th class="text-left">{{ $t('Documents') }}</th>
                <th class="text-left">{{ $t('Actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="records.length">
                <template v-for="(record, index) in records">
                  <template>
                    <tr
                      :key="`record-${index}`"
                      :class="`no-hover ${
                        record.lastComments?.length ? 'no-border-bottom' : ''
                      }`"
                    >
                      <td class="font-weight-bold">
                        {{ utcToLocalDateTimeLess(record.created_at) }}
                      </td>
                      <td class="font-weight-bold">{{ record.result }}</td>
                      <td v-html="record.detail" />
                      <td>
                        <single-file-item
                          v-for="file in record.files"
                          :key="`file-${file.id}`"
                          :file="file"
                        />
                      </td>
                      <td>
                        <div class="actions">
                          <v-btn
                            icon
                            v-if="showComment"
                            @click="() => showRecordComments(record.id)"
                            class="record-comment-btn"
                          >
                            <v-icon>mdi-comment</v-icon>
                            <div
                              class="record-comment-count"
                              v-if="record.lastComments?.length"
                            >
                              {{ record.lastComments.length }}
                            </div>
                          </v-btn>
                          <v-btn
                            icon
                            v-if="showDelete"
                            @click="() => handleDeleteRecord(record.id)"
                          >
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                    <tr
                      :key="`comment-${index}`"
                      v-if="getLast3Comments(record.lastComments).length"
                      class="no-hover"
                    >
                      <td colspan="5" class="no-padding">
                        <div class="record-comments">
                          <div
                            v-for="comment in getLast3Comments(
                              record.lastComments
                            )"
                            :key="`comment-${comment.id}`"
                            class="record-comment"
                          >
                            <div class="content">
                              <read-more
                                :text="comment.content"
                                :length="300"
                              />
                            </div>
                            <div class="files-and-time">
                              <div class="files">
                                <single-file-item
                                  v-for="file in comment.files"
                                  :key="`file-${file.id}`"
                                  :file="file"
                                  class="my-0 mr-0"
                                />
                              </div>
                              <div class="time">
                                {{ comment.user.name }} |
                                {{ utcToLocalDateTimeLess(comment.created_at) }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </template>
                </template>
              </template>
              <template v-else>
                <tr>
                  <td colspan="5">{{ $t('No data') }}</td>
                </tr>
              </template>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>
    </v-card>

    <div
      v-if="showPopupComment && !!this.taskRecordIdSelected"
      class="popup-comment-wrapper"
    >
      <div class="popup-comment-container">
        <div class="popup-comment-bg" @click="() => hideRecordComments()" />
        <div class="popup-comment-content">
          <comment obj="task_records" :obj-id="this.taskRecordIdSelected" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { utcToLocalDateTimeLess } from '@utils'
import SingleFileItem from '@/components/SingleFileItem'
import { call } from 'vuex-pathify'
import Comment from './Comment'
import ReadMore from '@/components/ReadMore'

export default {
  name: 'WorkProcessRecord',
  props: {
    taskId: {
      type: Number,
      required: false
    },
    records: {
      type: Array,
      required: true
    },
    showComment: {
      type: Boolean,
      default: true
    },
    showDelete: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      showPopupComment: false,
      taskRecordIdSelected: null
    }
  },
  components: {
    SingleFileItem,
    Comment,
    ReadMore
  },
  methods: {
    utcToLocalDateTimeLess,
    deleteRecord: call('task-record/delete'),
    getTaskRecords: call('task/getTaskRecords'),

    showRecordComments(objId) {
      this.taskRecordIdSelected = objId
      this.showPopupComment = true
      window.document.documentElement.style.overflow = 'hidden'
    },
    hideRecordComments() {
      this.showPopupComment = false
      this.taskRecordIdSelected = null
      window.document.documentElement.style.overflow = 'auto'
      this.taskId && this.getTaskRecords(this.taskId)
    },
    handleDeleteRecord(recordId) {
      if (confirm('Are you sure you want to delete this record?')) {
        this.deleteRecord(recordId)
      }
    },
    getLast3Comments(comments) {
      return comments?.slice(0, 4) ?? []
    }
  }
}
</script>

<style scoped lang="scss">
.actions {
  display: flex;
  gap: 4px;
  white-space: nowrap;
}
.popup-comment-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 10;
}
.popup-comment-container {
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 10;
  display: flex;
}

.popup-comment-bg {
  flex: 1;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
}

.popup-comment-content {
  width: 80%;
  max-width: 700px;
  max-height: 100%;
  height: 100%;
  background: #fafafa;
  padding: 16px;
  border-left: 1px solid #333;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
}
.theme--dark {
  .popup-comment-content {
    background: #000;
  }
}

.record-comments {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 8px 8px 50px;
  .record-comment {
    border: 1px dashed #666;
    padding: 8px 16px;
    display: flex;
    width: 100%;
    flex-direction: column;
    gap: 2px;
    border-radius: 8px;
    .content {
      font-size: 14px;
    }
    .files-and-time {
      display: flex;
      align-items: center;
      align-self: flex-end;
      gap: 6px;
    }
    .files {
      display: flex;
      gap: 4px;
      .mr-0 {
        margin-right: 0 !important;
      }
      .my-0 {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
      }
    }
    .time {
      text-align: right;
      font-size: 12px;
      color: #555;
    }
  }
}
.no-hover {
  &:hover {
    background-color: transparent !important;
  }
}
.no-padding {
  padding: 0 !important;
}
.no-border-bottom {
  td {
    border-bottom: none !important;
  }
}
.record-comment-btn {
  position: relative;
}
.record-comment-count {
  position: absolute;
  top: -6px;
  right: 0px;
  background-color: rgba(197, 5, 5, 1);
  font-size: 12px;
  font-weight: bold;
  color: #fff;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
